import axios, { AxiosError } from 'axios';

// Relative path - same-origin in production (nginx serves the built SPA and
// proxies /api to the backend on the same port), and proxied to the local
// backend by vite.config.ts's dev server for `npm run dev`.
export const api = axios.create({
  baseURL: '/api',
});

export const TOKEN_KEY = 'atlas_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Listeners the AuthContext registers so a 401 anywhere in the app (expired
// token, revoked session) can force a clean logout + redirect rather than
// leaving the UI in a half-authenticated state.
type UnauthorizedListener = () => void;
let unauthorizedListener: UnauthorizedListener | null = null;
export function onUnauthorized(listener: UnauthorizedListener) {
  unauthorizedListener = listener;
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      unauthorizedListener?.();
    }
    return Promise.reject(error);
  },
);

// Backend error responses are consistently { message, error, statusCode } -
// class-validator failures return message as a string[] instead of string.
export function apiErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    if (Array.isArray(data?.message)) {
      return data.message.join(', ');
    }
    if (typeof data?.message === 'string') {
      return data.message;
    }
    if (err.message) {
      return err.message;
    }
  }
  return fallback;
}
